/**
 * Centralized environment configuration.
 *
 * Validates required variables at startup and provides
 * typed access. In production, missing variables produce
 * a descriptive error. In development, a warning is logged.
 */

export type AppEnvironment = 'development' | 'production' | 'test';

interface EnvSchema {
  VITE_SUPABASE_URL: string;
  VITE_SUPABASE_ANON_KEY: string;
  VITE_EMAIL_SENDER?: string;
  VITE_EMAIL_REPLY_TO?: string;
  VITE_EMAIL_SUPPORT_EMAIL?: string;
  VITE_EMAIL_BRAND_NAME?: string;
  VITE_EMAIL_LOGO_URL?: string;
  VITE_EMAIL_PRIMARY_COLOR?: string;
  VITE_EMAIL_APP_URL?: string;
}

const REQUIRED_VARS: (keyof EnvSchema)[] = [
  'VITE_SUPABASE_URL',
  'VITE_SUPABASE_ANON_KEY',
  'VITE_EMAIL_SENDER',
  'VITE_EMAIL_REPLY_TO',
  'VITE_EMAIL_SUPPORT_EMAIL',
  'VITE_EMAIL_APP_URL',
  'VITE_EMAIL_BRAND_NAME',
];
const PROD_REQUIRED_VARS: (keyof EnvSchema)[] = REQUIRED_VARS;

class EnvironmentConfig {
  private values: Partial<EnvSchema> = {};
  private validated = false;

  get mode(): AppEnvironment {
    return import.meta.env.MODE as AppEnvironment;
  }

  get isProduction(): boolean {
    return this.mode === 'production';
  }

  get isDevelopment(): boolean {
    return this.mode === 'development';
  }

  get isTest(): boolean {
    return this.mode === 'test';
  }

  get supabaseUrl(): string {
    this.validate();
    return this.values.VITE_SUPABASE_URL!;
  }

  get supabaseAnonKey(): string {
    this.validate();
    return this.values.VITE_SUPABASE_ANON_KEY!;
  }

  getMissing(): string[] {
    const vars = this.isProduction ? PROD_REQUIRED_VARS : REQUIRED_VARS;
    return vars.filter((key) => {
      const value = import.meta.env[key];
      return !value || typeof value !== 'string' || value.trim() === '';
    });
  }

  validate(): void {
    if (this.validated) return;

    this.values = {
      VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL,
      VITE_SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY,
    };

    const missing = this.getMissing();

    if (missing.length > 0) {
      const message = `Missing required environment variables: ${missing.join(', ')}. Add them to your .env file.`;
      throw new Error(message);
    }

    this.validated = true;
  }
}

export const env = new EnvironmentConfig();
